import { Fragment, useState } from "react";
import { isUiMockMode } from "@/config/features";
import { canDownloadZip } from "@/domain/export/canDownloadZip";
import { exportZipLinkLabel } from "@/domain/export/exportZipLinkLabel";
import { sortedExportFiles } from "@/domain/export/sortedExportFiles";
import { BillingGateModal } from "@/features/billing/components/BillingGateModal";
import { usePlanAccess } from "@/features/billing/hooks/usePlanAccess";
import {
  downloadErrorMessage,
  useExportDownload,
} from "@/features/exports/hooks/useExportDownload";
import type { ExportJobDetail } from "@/types/api";

export function ExportDownloadCell({ job }: { job: ExportJobDetail }) {
  const download = useExportDownload();
  const { withGate } = usePlanAccess();
  const mock = isUiMockMode();
  const [gateReason, setGateReason] = useState<
    "subscription_required" | "upgrade_to_pro_required" | null
  >(null);

  const files = sortedExportFiles(job);
  const showDownloads = canDownloadZip(job.status, job.invoice_count) && files.length > 0;

  const onDownload = (fileId: number) => {
    if (mock) {
      alert("Mock mode — ZIP download is not wired to the backend.");
      return;
    }
    const reason = withGate("redownload_history", () =>
      download.mutate(
        { jobId: job.job_id, fileId },
        { onError: (e) => alert(downloadErrorMessage(e)) },
      ),
    );
    if (reason) setGateReason(reason);
  };

  const isFileLoading = (fileId: number) =>
    download.isPending && download.variables?.fileId === fileId;

  if (!showDownloads) {
    return <span className="text-sm text-[var(--muted)]">—</span>;
  }

  return (
    <>
      <div className="export-download-cell">
        {files.map((file, index) => (
          <Fragment key={file.file_id}>
            {index > 0 ? <span className="export-zip-sep" aria-hidden="true">, </span> : null}
            <button
              type="button"
              className="export-zip-link"
              disabled={isFileLoading(file.file_id)}
              title={file.file_name}
              onClick={() => onDownload(file.file_id)}
            >
              {exportZipLinkLabel(file.volume_index, files.length)}
            </button>
          </Fragment>
        ))}
      </div>
      {download.error ? (
        <span className="sr-only">{downloadErrorMessage(download.error)}</span>
      ) : null}
      <BillingGateModal
        open={gateReason != null}
        reason={gateReason}
        capability="redownload_history"
        onClose={() => setGateReason(null)}
      />
    </>
  );
}
